import React from 'react';
import PropTypes from 'prop-types';
import { DragDropContext } from 'react-beautiful-dnd';

import { imageValidation } from 'utils/admin-products';
import { AdminImageUpload } from 'shared-components';
import GalleryImages from './GalleryImages';
import PrivateImages from './PrivateImages';
import styles from './VariantImages.module.css';

const removeAtIndex = (list, index) => list.splice(index, 1);

const insertAtIndex = (list, index, item) => list.splice(index, 0, item);

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const VariantImages = ({
  galleryUrls,
  privateImageUrls,
  imageUrl,
  setFormData,
  thumbnailError,
}) => {
  const handleUpdateThumbnailImage = (jpeg, png) => {
    setFormData((prevState) => {
      return {
        ...prevState,
        imageUrl: png,
        privateImageUrls: prevState.privateImageUrls
          ? [...prevState.privateImageUrls, jpeg]
          : [jpeg],
      };
    });
  };

  const handleRemoveThumbnailImage = () => {
    setFormData((prevState) => {
      return {
        ...prevState,
        imageUrl: '',
      };
    });
  };

  const handleUpdateGalleryUrls = (jpegs, pngs) => {
    setFormData((prevState) => {
      return {
        ...prevState,
        galleryUrls: prevState.galleryUrls
          ? [...prevState.galleryUrls, ...pngs]
          : [...pngs],
        privateImageUrls: prevState.privateImageUrls
          ? [...prevState.privateImageUrls, ...jpegs]
          : [...jpegs],
      };
    });
  };

  const handleRemoveImage = (index, field) => {
    setFormData((prevState) => {
      const newImages = [...prevState[field]];
      removeAtIndex(newImages, index);
      return {
        ...prevState,
        [field]: newImages,
      };
    });
  };

  const onDragEnd = (result) => {
    const { destination } = result;
    // dropped outside the list
    if (!destination) {
      return;
    }
    const {
      destination: { droppableId: destinationDroppableId },
      source: { droppableId: sourceDroppableId },
      source,
    } = result;
    // prevent drag and drop from gallery to thumbnail
    if (
      destinationDroppableId === 'imageUrl' &&
      sourceDroppableId === 'galleryUrls'
    ) {
      return;
    }
    setFormData((prevState) => {
      const destinationItems = prevState[destinationDroppableId]
        ? [...prevState[destinationDroppableId]]
        : [];
      if (destinationDroppableId === sourceDroppableId) {
        const newItems = reorder(
          destinationItems,
          source.index,
          destination.index
        );
        return {
          ...prevState,
          [destinationDroppableId]: newItems,
        };
      }
      const sourceItems = prevState[sourceDroppableId]
        ? [...prevState[sourceDroppableId]]
        : [];
      const [removed] = removeAtIndex(sourceItems, source.index);
      insertAtIndex(destinationItems, destination.index, removed);
      return {
        ...prevState,
        [destinationDroppableId]:
          destinationDroppableId === 'imageUrl' ? removed : destinationItems,
        [sourceDroppableId]: sourceItems,
      };
    });
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className={styles.container}>
        <AdminImageUpload
          error={thumbnailError}
          imageUrl={imageUrl}
          updateImageUrl={handleUpdateThumbnailImage}
          removeThumbnailImage={handleRemoveThumbnailImage}
          uploadText="Upload Thumbnail Image"
          labelText="Thumbnail Image"
          validateImage={imageValidation}
          name="imageInput"
        />
        <GalleryImages
          galleryUrls={galleryUrls}
          validateImage={imageValidation}
          handleUpload={handleUpdateGalleryUrls}
          handleRemove={handleRemoveImage}
        />
      </div>
      <PrivateImages
        images={privateImageUrls}
        handleRemove={handleRemoveImage}
      />
    </DragDropContext>
  );
};

VariantImages.propTypes = {
  galleryUrls: PropTypes.arrayOf(PropTypes.string),
  privateImageUrls: PropTypes.arrayOf(PropTypes.string),
  imageUrl: PropTypes.string,
  setFormData: PropTypes.func.isRequired,
  thumbnailError: PropTypes.string,
};

VariantImages.defaultProps = {
  imageUrl: '',
  galleryUrls: [],
  privateImageUrls: [],
  thumbnailError: '',
};

export default VariantImages;
